import React from 'react';
import { Heart, Users, Shield } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

export const WhyUsSection = () => {
  return (
    <section id="why-us" className="section section-alt" data-testid="why-us-section">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6" style={{ color: 'var(--sage-dark)' }}>Why Choose Us</h2>
            <p className="text-xl font-semibold mb-8" style={{ color: 'var(--earth-accent)' }}>
              True Continuity of Care Across Every Stage of Life
            </p>
          </div>

          <Card className="mb-8">
            <CardContent className="p-8 md:p-10">
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  A patient should choose <strong style={{ color: 'var(--sage-dark)' }}>Tri-City Internists</strong> because
                  it offers something rare in modern medicine: <strong>true continuity of care across every stage of adulthood
                  and aging</strong>. Instead of being passed from office to hospital to nursing facility to hospice—often seeing
                  new faces each time—patients are cared for by a team that knows them, follows them, and advocates for them
                  wherever they are.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Tri-City Internists provides comprehensive internal medicine for adults and geriatrics, managing both routine
                  and complex conditions with a proactive, long-term approach. Whether a patient is seen in the office for
                  preventive care, admitted to the hospital, residing in a nursing facility, or transitioning to hospice,
                  their care remains <strong style={{ color: 'var(--sage-dark)' }}>coordinated, consistent, and personal</strong>.
                  This continuity leads to better outcomes, fewer gaps in care, and greater peace of mind for both patients and families.
                </p>

                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  For older adults especially, this full-spectrum model means <strong style={{ color: 'var(--sage-dark)' }}>dignity,
                  clarity, and trust</strong>. Medical decisions are made with deep understanding of the patient&apos;s history, values,
                  and goals—not just isolated symptoms. Families gain reassurance knowing there is one medical team overseeing the
                  entire journey, communicating clearly, and focusing on quality of life as much as clinical excellence.
                </p>

                <div className="p-6 rounded-lg mt-8" style={{ backgroundColor: 'rgba(74, 124, 89, 0.1)', borderLeft: '4px solid var(--sage-primary)' }}>
                  <p className="text-xl font-semibold text-center" style={{ color: 'var(--sage-dark)' }}>
                    In short, Tri-City Internists isn&apos;t just treating illnesses—it&apos;s partnering with patients through
                    every chapter of adult life, delivering compassionate, comprehensive care exactly where and when it&apos;s needed.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {[
              { icon: Heart, title: 'Continuous Care', text: 'From office visits to hospital, nursing facilities, and hospice—one team throughout your journey.' },
              { icon: Users, title: 'Coordinated Approach', text: 'Better outcomes through seamless communication and coordinated care across all settings.' },
              { icon: Shield, title: 'Peace of Mind', text: 'Families rest assured knowing one trusted team oversees the entire care journey.' }
            ].map(({ icon: Icon, title, text }) => (
              <Card key={title} className="text-center border-t-4 hover:shadow-lg transition-shadow" style={{ borderTopColor: 'var(--sage-primary)' }}>
                <CardContent className="p-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(74, 124, 89, 0.1)' }}>
                    <Icon className="w-8 h-8" style={{ color: 'var(--sage-primary)' }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--sage-dark)' }}>{title}</h3>
                  <p className="text-gray-600 text-sm">{text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
